<script setup>
// Content only — BannerCarousel controls visibility
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { usePermissions, PERMISSIONS } from '@/composables/usePermissions'
import { useBillingStore } from '@/stores/billing'
import { toneOf } from '@/helpers/billing-status-alert'

const router = useRouter()
const { can } = usePermissions()
const billingStore = useBillingStore()

const statusAlert = computed(() => billingStore.statusAlert)
const kind = computed(() => statusAlert.value?.kind ?? null)
const canManageBilling = computed(() => can(PERMISSIONS.BILLING_UPDATE))

const usageLabel = computed(() =>
    (statusAlert.value?.usage ?? 0).toLocaleString()
)
const limitLabel = computed(() =>
    (statusAlert.value?.limit ?? 0).toLocaleString()
)

// "today", "in 1 day", "in 5 days"
const trialEndsIn = computed(() => {
    const days = statusAlert.value?.days_left ?? 0
    if (days <= 0) return 'today'
    return days === 1 ? 'in 1 day' : `in ${days} days`
})

const trialNeedsCard = computed(
    () =>
        kind.value === 'trial_ending' &&
        statusAlert.value?.outcome === 'trial_free_no_card'
)

// The button is hidden below `sm`: the banner is one fixed-height line, and the
// message needs the width. The billing page carries the same alert and action.
const cta = computed(() => {
    switch (kind.value) {
        case 'suspended':
        case 'past_due':
        case 'card_expiring':
            return canManageBilling.value
                ? { label: 'Update payment method', paymentMethod: true }
                : null
        case 'action_required':
            return statusAlert.value.authentication_url
                ? {
                      label: 'Confirm payment',
                      href: statusAlert.value.authentication_url,
                  }
                : null
        case 'trial_ending':
            return trialNeedsCard.value && canManageBilling.value
                ? { label: 'Add payment method', paymentMethod: true }
                : { label: 'View billing' }
        case 'over_mac_limit':
            return canManageBilling.value ? { label: 'Upgrade plan' } : null
        default:
            return null
    }
})

const ctaClass = computed(() => {
    switch (toneOf(statusAlert.value)) {
        case 'critical':
            return 'bg-white text-red-700 hover:bg-red-50'
        case 'warning':
            return 'bg-white text-amber-700 hover:bg-amber-50'
        default:
            return 'bg-sky-600 text-white hover:bg-sky-700'
    }
})

function goToBilling() {
    const route = { name: 'AdminBillingScreen' }
    if (cta.value?.paymentMethod) {
        route.query = { openPaymentMethod: '1' }
    }
    router.push(route).catch(() => {})
}
</script>

<template>
    <div
        v-if="statusAlert"
        class="flex w-full items-center justify-between gap-3 min-w-0"
        :data-testid="`billing-status-banner-${kind}`"
    >
        <span class="line-clamp-2">
            <template v-if="kind === 'suspended'">
                Your account is suspended over an unpaid invoice. Update your
                payment method to restore it.
            </template>
            <template v-else-if="kind === 'past_due'">
                We couldn't charge your card.
                <template v-if="statusAlert.grace_ends_at">
                    Update it by
                    <zen-date-time
                        :value="statusAlert.grace_ends_at"
                        format="date"
                        utc
                    />
                    or we'll suspend your account.
                </template>
                <template v-else>
                    Update your payment method or we'll suspend your account.
                </template>
            </template>
            <template v-else-if="kind === 'action_required'">
                Your bank needs you to confirm a payment before it goes through.
            </template>
            <template v-else-if="kind === 'trial_ending'">
                <template v-if="trialNeedsCard">
                    Your trial ends {{ trialEndsIn }}. No card on file, so
                    you'll move to Free. Add one to keep your plan.
                </template>
                <template v-else-if="statusAlert.outcome === 'trial_charge'">
                    Your trial ends {{ trialEndsIn }}. We'll charge your card
                    then.
                </template>
                <template
                    v-else-if="
                        statusAlert.outcome === 'trial_free_plan' ||
                        statusAlert.outcome === 'cancels_to_free'
                    "
                >
                    Your trial ends {{ trialEndsIn }}, then you move to Free.
                </template>
                <template v-else>Your trial ends {{ trialEndsIn }}.</template>
            </template>
            <template v-else-if="kind === 'over_mac_limit'">
                You've used {{ usageLabel }} of {{ limitLabel }} monthly active
                contexts on your plan. You're over your limit.
            </template>
            <template v-else-if="kind === 'card_expiring'">
                Your {{ statusAlert.description || 'card' }} expires on
                <zen-date-time
                    :value="statusAlert.expires_on"
                    format="date"
                    utc
                />. Update it so your next payment goes through.
            </template>
        </span>
        <a
            v-if="cta?.href"
            :href="cta.href"
            data-testid="billing-status-banner-cta"
            class="hidden shrink-0 cursor-pointer rounded-md px-3 py-1 text-sm font-medium sm:inline-block"
            :class="ctaClass"
        >
            {{ cta.label }}
        </a>
        <button
            v-else-if="cta"
            type="button"
            data-testid="billing-status-banner-cta"
            class="hidden shrink-0 cursor-pointer rounded-md px-3 py-1 text-sm font-medium sm:inline-block"
            :class="ctaClass"
            @click="goToBilling"
        >
            {{ cta.label }}
        </button>
    </div>
</template>
